import React, {useEffect, useMemo, useRef} from 'react';
import SourceDestination from '../CreateRecommend/RecommendSourceDestination/SourceDestination';
import {RecommendProps} from '../CreateRecommend/RecommendSourceDestination/validators';
import {RecommendPlanFormsStyled} from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import {
  ContributeDestinationPlanForm,
  ContributeNewPlanForm,
} from './ContributeDestinationPlanForm';
import {ContributeSourcePlanForm} from './ContributeSourcePlanForm';
import {CASH_OBJ} from '../../../utils/potsFilter';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {contributeFieldType, contributeDestinationValuesType} from './contributeFields';
import type {setUpNewFields} from '../RecommendsSetUpNew/setUpNewFields';
import {Pot} from '../../../schema-types/Pot';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {useResetReccs} from '../CreateRecommend/RecommendSourceDestination/useResetReccs';

import RecommendParserStep from '../RecommendParserStep/RecommendParserStep';
import {contributionSources, contributionDestinations} from './ContributeHelpers';
import {
  updateContributionAmounts,
  syncContributeNewPlanSubplanAmounts,
  changeContributeFromFactory,
  changeContributeToFactory,
  getAllContributePots,
  loadSavedContribution,
} from '../hooks/contributeHooks';
import {
  useRecommendContext,
  useRecommendPlanTypes,
} from '../CreateRecommend/CreateRecommendHelpers';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import {UPDATES} from '../../../shared/UPDATES';
import {
  useAutoForm,
  useAutoField,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
// import {useResetContributeAmounts} from '../hooks/contributeHooks';

/**
 * PLEASE NOTE: source pots: "contribute from" and destination pots: "contribute to"
 */
const Contribute: React.FC<RecommendProps> = ({
  sourcePots,
  sourceDestinationLabels,
  destinationPots,
  contactName,
}) => {
  const {loaded, setLoaded} = useLoadedAutoField();
  const loadedRecommendIdRef = useRef<string | null>(null);
  const {recommendDetails, isSPPDA, recommendReviewId} = useRecommendContext();
  const {values} = useAutoForm<recommendFieldsType>();
  const [contributeFieldObj, contributeMeta, contributeHelpers] = useAutoField<
    recommendFieldsType['contribute'] | undefined
  >('contribute');
  useResetReccs(OVRecommendActionEnum.CONTRIBUTE);
  // useResetContributeAmounts();
  const [contributeFromObj, contributeFromMeta, contributeFromHelpers] = useAutoField<
    contributeFieldType['contributeFrom']
  >('contribute.contributeFrom');
  const [destinationValuesFieldObj, destinationValuesMeta, destinationValuesHelpers] =
    useAutoField<contributeDestinationValuesType>('contribute.destinationValues');
  const {mainPlanTypes} = useRecommendPlanTypes(values);
  const [contributeToFieldObj, contributeToMeta, contributeToHelpers] = useAutoField<
    contributeFieldType['contributeTo'] | undefined
  >('contribute.contributeTo');
  const [setUpNewFieldObj, setUpNewFieldMeta, setUpNewFieldHelpers] = useAutoField<
    setUpNewFields['newPlan'] | undefined
  >('contribute.newPlan');
  const contributeFromPots = [
    CASH_OBJ as Pot,
    // ensure that current accounts are not available to select as a source for a contribution
    ...destinationPots.filter((x) => !x.productIsBank),
  ];
  const sourcePotsList = useMemo(() => {
    const pots = contributionSources({
      contributeFromPots,
      contributeTo: contributeToFieldObj.value,
      isSppDa: isSPPDA,
    });
    // hide new plans
    return pots.filter((pot) => pot.id !== values.contribute?.newPlan?.existingPotId);
  }, [
    contributeFromPots,
    contributeToFieldObj.value,
    isSPPDA,
    values.contribute?.newPlan?.existingPotId,
  ]);
  const changeContributeFrom = changeContributeFromFactory({
    contributeFromPots,
    contributeFromHelpers,
  });
  const allPots = getAllContributePots({sourcePots, destinationPots, recommendDetails});
  const changeContributeTo = changeContributeToFactory({
    allPots,
    contributeFieldObj,
    contributeFromObj,
    contributeHelpers,
  });
  useEffect(() => {
    if (values.forcedMainPlanType) {
      // coming from set up new and choosing cash if nothing is chosen
      changeContributeFrom(null, CASH_OBJ.id);
      changeContributeTo(null, NEW_PLAN_ID);
    }
  }, [values.forcedMainPlanType]);

  const destPots = useMemo(() => {
    const pots = contributionDestinations({
      sourcePots,
      contributeFieldObj,
      recommendDetails,
      isSPPDA,
    });
    return pots;
  }, [
    sourcePots,
    contributeFieldObj.value?.contributeFrom?.id,
    recommendDetails,
    isSPPDA,
  ]);

  useEffect(() => {
    updateContributionAmounts({
      values,
      destinationValuesHelpers,
      destinationValuesFieldObj,
      contributeFromFieldObj: contributeFromObj,
    });
    // keeps new-plan subplan amount fields in sync with the source
    syncContributeNewPlanSubplanAmounts({
      contributeFromFieldObj: contributeFromObj,
      setUpNewFieldObj,
      setUpNewFieldHelpers,
    });
  }, [
    contributeFromObj?.value?.singleClientAmount,
    contributeFromObj?.value?.recurringClientAmount,
    contributeFromObj?.value?.recurringEmployerAmount,
    contributeFromObj?.value?.singleEmployerAmount,
    values.contribute?.contributeTo,
    setUpNewFieldObj?.value?.subplan0?.checked,
    setUpNewFieldObj?.value?.subplan1?.checked,
  ]);

  useEffect(() => {
    console.log('Contribute useEffect running', {
      hasContributeTo: !!contributeFieldObj.value?.contributeTo,
      loaded,
      recommendDetailsId: recommendDetails?.id,
      loadedRecommendIdRef: loadedRecommendIdRef.current,
    });

    if (contributeFieldObj.value?.contributeTo) {
      // if we've gotten to contirbute by set up new plan, ignore
      console.log('Already has contributeTo, setting loaded');
      setLoaded();
      return;
    }
    if (loaded) {
      console.log('Already loaded, skipping');
      return;
    }
    // Check if we've already loaded this specific recommendation
    const currentRecommendId = recommendDetails?.id || null;
    if (
      loadedRecommendIdRef.current === currentRecommendId &&
      currentRecommendId !== null
    ) {
      console.log('Already loaded this recommendation ID, skipping');
      return;
    }
    // we can assume if there's a recommendDetails, there must be sourcePots for it to use
    console.log('Loading saved contribution');
    const newContribute = loadSavedContribution({
      recommendDetails,
      allPots,
      isSPPDA,
    });
    if (newContribute) {
      console.log('Setting new contribute from loadSavedContribution');
      contributeHelpers.setValue(UPDATES._293, newContribute);
    }
    loadedRecommendIdRef.current = currentRecommendId;
    setLoaded();
  }, [recommendDetails, sourcePots, contributeFieldObj.value?.contributeTo, loaded]);

  if (!values.mainPlanType) {
    return null;
  }
  if (!mainPlanTypes.length) {
    return <RecommendParserStep errorText="There are no plans to contribute from." />;
  }

  return (
    <SourceDestination
      fromError={contributeFromMeta.error}
      toError={contributeToMeta.error}
      source={
        <RecommendPlanFormsStyled>
          {sourcePotsList.map((pot) => (
            <ContributeSourcePlanForm
              key={pot.id}
              pot={pot}
              contributeFromPots={contributeFromPots}
              contributeFieldObj={contributeFieldObj}
              contributeFromMeta={contributeFromMeta}
              contributeTo={contributeToFieldObj.value}
              reviewId={recommendReviewId}
              handleChange={changeContributeFrom}
            />
          ))}
        </RecommendPlanFormsStyled>
      }
      destination={
        <RecommendPlanFormsStyled>
          {destPots.map((pot) => (
            <ContributeDestinationPlanForm
              key={pot.id}
              pot={pot}
              destinationPots={destinationPots}
              values={values}
              contributeFieldObj={contributeFieldObj}
              contributeToMeta={contributeToMeta}
              recommendDetails={recommendDetails}
              reviewId={recommendReviewId}
              handleChange={changeContributeTo}
            />
          ))}
          <ContributeNewPlanForm
            contributeFieldObj={contributeFieldObj}
            contactName={contactName}
            setUpNewFieldMeta={setUpNewFieldMeta}
            recommendDetails={recommendDetails}
            handleChange={changeContributeTo}
          />
        </RecommendPlanFormsStyled>
      }
      sourceLabel={sourceDestinationLabels?.first}
      destinationLabel={sourceDestinationLabels?.second}
    />
  );
};

export default Contribute;
